import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { usePoolAvailability, usePoolBookings, usePoolProducts, usePoolSettings, useSvcMutation, type PoolBooking } from '../api/services';
import { addDays, formatDate, formatGhs, parseCedis, type Tone } from '../format';
import { Badge, Button, Empty, ErrorNote, Field, Loading, Modal, useReport, useToast } from '../ui/kit';
import { PageHead } from './shared';
import { InlinePrice, ServicePayDialog } from './ServicePay';
import { RefundDialog } from './Refund';

const STATUS: Record<PoolBooking['status'], { label: string; tone: Tone }> = {
  BOOKED: { label: 'Booked', tone: 'info' },
  ARRIVED: { label: 'Arrived', tone: 'good' },
  CANCELLED: { label: 'Cancelled', tone: '' },
  NO_SHOW: { label: 'No-show', tone: 'bad' },
};

export function Pool() {
  const { me, can } = useAuth();
  const today = me?.property.today ?? '';
  const [date, setDate] = useState(today);
  const avail = usePoolAvailability(date);
  const list = usePoolBookings(date);
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState<null | { walkIn: boolean }>(null);
  const [setup, setSetup] = useState(false);

  const bookings = list.data ?? [];
  const open = bookings.find((b) => b.id === openId) ?? null;
  const live = bookings.filter((b) => b.status === 'BOOKED' || b.status === 'ARRIVED');
  const people = live.reduce((s, b) => s + b.people, 0);
  const takings = live.filter((b) => b.paid).reduce((s, b) => s + b.total, 0);

  return (
    <div className="page">
      <PageHead eyebrow="Pool" title="Pool bookings">
        {can('pool.book') ? (
          <>
            {date === today ? <Button onClick={() => setCreating({ walkIn: true })}>Walk-in</Button> : null}
            <Button variant="primary" onClick={() => setCreating({ walkIn: false })}>
              New booking
            </Button>
          </>
        ) : null}
        {can('pool.manage') ? <Button onClick={() => setSetup(true)}>Hours and prices</Button> : null}
      </PageHead>

      <div className="row">
        <Button aria-label="Previous day" onClick={() => setDate(addDays(date, -1))}>
          ←
        </Button>
        <input className="input" type="date" aria-label="Day" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} style={{ maxWidth: 190 }} />
        <Button aria-label="Next day" onClick={() => setDate(addDays(date, 1))}>
          →
        </Button>
        {date !== today ? (
          <Button variant="ghost" onClick={() => setDate(today)}>
            Today
          </Button>
        ) : null}
        <span className="small muted">{formatDate(date, true)}</span>
      </div>

      <section className="stats" aria-label="Pool today">
        <div className="card stat">
          <span className="eyebrow">People booked</span>
          <span className="figure num">{people}</span>
        </div>
        <div className="card stat">
          <span className="eyebrow">Bookings</span>
          <span className="figure num">{live.length}</span>
        </div>
        <div className="card stat">
          <span className="eyebrow">Paid so far</span>
          <span className="figure num">{formatGhs(takings)}</span>
        </div>
      </section>

      {avail.data ? (
        <div className="slots" role="list" aria-label="Sessions">
          {avail.data.slots.map((s) => (
            <div key={s.slot} role="listitem" className={`slot${s.remaining === 0 ? ' full' : ''}`}>
              <strong className="num">{s.slot}</strong>
              <span className="small">{s.remaining === 0 ? 'Full' : `${s.remaining} left`}</span>
            </div>
          ))}
        </div>
      ) : null}

      {list.isLoading ? (
        <Loading rows={3} />
      ) : list.error ? (
        <ErrorNote error={list.error} retry={() => void list.refetch()} />
      ) : bookings.length === 0 ? (
        <div className="card flat">
          <Empty icon="inbox" title="No pool bookings for this day" />
        </div>
      ) : (
        <div className="list">
          {bookings.map((b) => (
            <button key={b.id} type="button" className="item" style={{ textAlign: 'left', cursor: 'pointer' }} onClick={() => setOpenId(b.id)}>
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <span className="title">
                  <span className="num">{b.slot}</span> · {b.customerName}
                </span>
                <span className="row">
                  {b.refunded ? <Badge>Refunded</Badge> : b.paid ? <Badge tone="good">Paid</Badge> : b.status === 'BOOKED' || b.status === 'ARRIVED' ? <Badge tone="warn">Unpaid</Badge> : null}
                  <Badge tone={STATUS[b.status].tone}>{STATUS[b.status].label}</Badge>
                </span>
              </div>
              <span className="small muted">
                {b.people} {b.people === 1 ? 'person' : 'people'} · {b.productName} · {formatGhs(b.total)} · <span className="num">{b.reference}</span>
              </span>
            </button>
          ))}
        </div>
      )}

      <BookingDialog booking={open} onClose={() => setOpenId(null)} />
      {creating ? <NewBookingDialog date={date} walkIn={creating.walkIn} onClose={() => setCreating(null)} /> : null}
      {setup ? <SetupDialog onClose={() => setSetup(false)} /> : null}
    </div>
  );
}

/* ------------------------------------------------------------ one booking */
function BookingDialog({ booking: b, onClose }: { booking: PoolBooking | null; onClose: () => void }) {
  const { can } = useAuth();
  const toast = useToast();
  const report = useReport();
  const [paying, setPaying] = useState(false);
  const [refunding, setRefunding] = useState(false);
  const act = useSvcMutation((v: { path: string; reason?: string }) => api(`/pool/bookings/${b!.id}/${v.path}`, { method: 'POST', body: v.reason ? { reason: v.reason } : {} }));
  if (!b) return <Modal open={false} onClose={onClose} title="Booking">{null}</Modal>;
  const live = b.status === 'BOOKED' || b.status === 'ARRIVED';
  const run = (path: string, done: string) => act.mutate({ path }, { onError: report, onSuccess: () => toast.ok(done) });

  return (
    <>
      <Modal open={!paying && !refunding} onClose={onClose} title={`${b.slot} · ${b.customerName}`}>
        <div className="stack">
          <div className="row">
            <Badge tone={STATUS[b.status].tone}>{STATUS[b.status].label}</Badge>
            {b.refunded ? <Badge>Refunded</Badge> : b.paid ? <Badge tone="good">Paid by {b.paidWith?.toLowerCase()}</Badge> : live ? <Badge tone="warn">Not paid</Badge> : null}
          </div>
          <dl className="kv">
            <dt>Reference</dt>
            <dd className="num">{b.reference}</dd>
            <dt>People</dt>
            <dd>{b.people}</dd>
            <dt>Ticket</dt>
            <dd>{b.productName} · {formatGhs(b.unitPrice)} each</dd>
            <dt>Total</dt>
            <dd className="num">{formatGhs(b.total)}</dd>
            {b.phone ? (
              <>
                <dt>Phone</dt>
                <dd>
                  <a href={`tel:${b.phone}`}>{b.phone}</a>
                </dd>
              </>
            ) : null}
          </dl>
          {b.notes ? <p className="small muted">{b.notes}</p> : null}
          {can('pool.book') && b.status === 'BOOKED' ? (
            <Button variant="primary" block loading={act.isPending} onClick={() => run('arrive', 'Marked as arrived')}>
              Arrived
            </Button>
          ) : null}
          {can('refund.issue') && b.paid && !b.refunded ? (
            <Button variant="danger" block onClick={() => setRefunding(true)}>
              Refund {formatGhs(b.total)}
            </Button>
          ) : null}
          {can('service.settle') && live && !b.paid && b.total > 0 ? (
            <Button block onClick={() => setPaying(true)}>
              Take payment · {formatGhs(b.total)}
            </Button>
          ) : null}
          {can('pool.book') && b.status === 'BOOKED' ? (
            <div className="row">
              <Button variant="danger" loading={act.isPending} disabled={b.paid && !b.refunded} onClick={() => run('cancel', 'Booking cancelled')}>
                Cancel booking
              </Button>
              <Button variant="ghost" loading={act.isPending} onClick={() => run('no-show', 'Marked as no-show')}>
                No-show
              </Button>
            </div>
          ) : null}
          {b.paid && !b.refunded && b.status === 'BOOKED' ? <p className="hint">A paid booking is cancelled by refunding it. A manager does that.</p> : null}
          <Button block onClick={onClose}>
            Close
          </Button>
        </div>
      </Modal>
      {refunding ? <RefundDialog title={`Refund ${b.reference}`} path={`/pool/bookings/${b.id}/refund`} paid={[{ method: b.paidWith ?? 'CASH', amount: b.total }]} withCancel={b.status === 'BOOKED'} onClose={() => setRefunding(false)} /> : null}
      {paying ? <ServicePayDialog title={`Pay ${b.reference}`} payPath={`/pool/bookings/${b.id}/pay`} total={b.total} bookingId={b.bookingId} onClose={() => setPaying(false)} /> : null}
    </>
  );
}

/* ------------------------------------------------------------ new booking */
function NewBookingDialog({ date, walkIn, onClose }: { date: string; walkIn: boolean; onClose: () => void }) {
  const { me } = useAuth();
  const today = me?.property.today ?? date;
  const toast = useToast();
  const report = useReport();
  const [day, setDay] = useState(walkIn ? today : date);
  const avail = usePoolAvailability(day);
  const products = usePoolProducts();
  const [slot, setSlot] = useState('');
  const [people, setPeople] = useState(1);
  const [productId, setProductId] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const make = useSvcMutation((body: Record<string, unknown>) => api('/pool/bookings', { method: 'POST', body }));

  useEffect(() => {
    if (!productId && products.data?.[0]) setProductId(products.data[0].id);
  }, [products.data, productId]);
  useEffect(() => {
    // Choose a session that can take this group, whenever the day or group size changes. Today starts from the
    // session under way right now (hotel clock); other days start from the first one.
    const fits = (avail.data?.slots ?? []).filter((s) => s.remaining >= people);
    const now = day === today ? new Intl.DateTimeFormat('en-GB', { timeZone: me?.property.timezone ?? 'Africa/Accra', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(new Date()) : '';
    const current = now ? [...fits].reverse().find((s) => s.slot <= now) : undefined;
    const pick = current ?? fits.find((s) => !now || s.slot >= now) ?? fits[0];
    setSlot((cur) => (fits.some((s) => s.slot === cur) ? cur : (pick?.slot ?? '')));
  }, [avail.data, people, day, today, me?.property.timezone]);

  const product = products.data?.find((p) => p.id === productId);
  const valid = !!slot && !!productId && name.trim().length >= 2 && people >= 1;

  return (
    <Modal open onClose={onClose} title={walkIn ? 'Walk-in' : 'New pool booking'}>
      <form
        className="stack"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (!valid) return;
          make.mutate(
            { date: day, slot, people, productId, customerName: name.trim(), ...(phone.trim() ? { phone: phone.trim() } : {}), ...(walkIn ? { walkIn: true } : {}) },
            { onError: report, onSuccess: () => { toast.ok(walkIn ? 'Walk-in recorded' : 'Booked'); onClose(); } },
          );
        }}
      >
        {!walkIn ? (
          <Field id="pb-day" label="Day">
            <input id="pb-day" className="input" type="date" min={today} value={day} onChange={(e) => e.target.value && setDay(e.target.value)} />
          </Field>
        ) : null}
        <div className="grid-2">
          <Field id="pb-ppl" label="How many people?">
            <input id="pb-ppl" className="input" type="number" min={1} max={100} value={people} onChange={(e) => setPeople(Math.max(1, Number(e.target.value) || 1))} />
          </Field>
          <Field id="pb-prod" label="Ticket">
            <select id="pb-prod" className="select" value={productId} onChange={(e) => setProductId(e.target.value)}>
              {(products.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} · {formatGhs(p.price)}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field id="pb-slot" label="Session" hint={avail.data ? `Pool holds ${avail.data.capacity} at a time` : undefined}>
          <select id="pb-slot" className="select" value={slot} onChange={(e) => setSlot(e.target.value)}>
            {(avail.data?.slots ?? []).map((s) => (
              <option key={s.slot} value={s.slot} disabled={s.remaining < people}>
                {s.slot} · {s.remaining < people ? (s.remaining === 0 ? 'full' : `only ${s.remaining} left`) : `${s.remaining} left`}
              </option>
            ))}
          </select>
        </Field>
        <Field id="pb-name" label="Name">
          <input id="pb-name" className="input" value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" />
        </Field>
        <Field id="pb-phone" label="Phone (optional)">
          <input id="pb-phone" className="input" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </Field>
        {product ? (
          <div className="total-row">
            <span>Total</span>
            <span className="amount num">{formatGhs(product.price * people)}</span>
          </div>
        ) : null}
        {products.data && products.data.length === 0 ? <div className="banner bad">No tickets are set up yet. A manager can add them under Hours and prices.</div> : null}
        <div className="modal-foot">
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={!valid} loading={make.isPending}>
            {walkIn ? 'Record walk-in' : 'Book'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/* ----------------------------------------------------- hours, tickets, prices */
function SetupDialog({ onClose }: { onClose: () => void }) {
  const settings = usePoolSettings();
  const products = usePoolProducts(true);
  const toast = useToast();
  const report = useReport();
  const [opens, setOpens] = useState('');
  const [closes, setCloses] = useState('');
  const [slotMinutes, setSlotMinutes] = useState('60');
  const [capacity, setCapacity] = useState('20');
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const save = useSvcMutation((body: Record<string, unknown>) => api('/pool/settings', { method: 'PUT', body }));
  const add = useSvcMutation((body: Record<string, unknown>) => api('/pool/products', { method: 'POST', body }));
  const patch = useSvcMutation((v: { id: string; body: Record<string, unknown> }) => api(`/pool/products/${v.id}`, { method: 'PATCH', body: v.body }));

  useEffect(() => {
    if (settings.data) {
      setOpens(settings.data.opensAt);
      setCloses(settings.data.closesAt);
      setSlotMinutes(String(settings.data.slotMinutes));
      setCapacity(String(settings.data.capacity));
    }
  }, [settings.data]);

  const p = parseCedis(price);
  return (
    <Modal open onClose={onClose} title="Pool hours and prices">
      <div className="stack">
        <form
          className="stack"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            save.mutate({ opensAt: opens, closesAt: closes, slotMinutes: Number(slotMinutes), capacity: Number(capacity) }, { onError: report, onSuccess: () => toast.ok('Pool hours saved') });
          }}
        >
          <div className="eyebrow">Opening hours</div>
          <div className="grid-2">
            <Field id="ps-open" label="Opens">
              <input id="ps-open" className="input" type="time" value={opens} onChange={(e) => setOpens(e.target.value)} />
            </Field>
            <Field id="ps-close" label="Closes">
              <input id="ps-close" className="input" type="time" value={closes} onChange={(e) => setCloses(e.target.value)} />
            </Field>
            <Field id="ps-slot" label="Session length (minutes)">
              <input id="ps-slot" className="input" inputMode="numeric" value={slotMinutes} onChange={(e) => setSlotMinutes(e.target.value.replace(/\D/g, ''))} />
            </Field>
            <Field id="ps-cap" label="People at once">
              <input id="ps-cap" className="input" inputMode="numeric" value={capacity} onChange={(e) => setCapacity(e.target.value.replace(/\D/g, ''))} />
            </Field>
          </div>
          <Button type="submit" loading={save.isPending} disabled={!opens || !closes || !Number(slotMinutes) || !Number(capacity)}>
            Save hours
          </Button>
          <p className="hint">Bookings already made keep their time even if the hours change.</p>
        </form>

        <div className="eyebrow">Tickets and prices (GH₵ per person, tax included)</div>
        <ul className="checklist">
          {(products.data ?? []).map((x) => (
            <li key={x.id}>
              <div className="row" style={{ justifyContent: 'space-between', minHeight: 44 }}>
                <span className="row">
                  <span>
                    {x.name}
                    {x.active ? '' : ' (hidden)'}
                  </span>
                  <InlinePrice value={x.price} pending={patch.isPending} onSave={(price) => patch.mutate({ id: x.id, body: { price } }, { onError: report, onSuccess: () => toast.ok('Price updated for new bookings') })} />
                </span>
                <Button variant="ghost" onClick={() => patch.mutate({ id: x.id, body: { active: !x.active } }, { onError: report })}>
                  {x.active ? 'Hide' : 'Show'}
                </Button>
              </div>
            </li>
          ))}
        </ul>
        <form
          className="row"
          style={{ alignItems: 'flex-end' }}
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            if (name.trim().length >= 2 && p !== null) add.mutate({ name: name.trim(), price: p }, { onError: report, onSuccess: () => { toast.ok('Ticket added'); setName(''); setPrice(''); } });
          }}
        >
          <Field id="pp-name" label="New ticket">
            <input id="pp-name" className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Visitor day pass" />
          </Field>
          <Field id="pp-price" label="Price">
            <input id="pp-price" className="input" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} />
          </Field>
          <Button type="submit" disabled={name.trim().length < 2 || p === null} loading={add.isPending}>
            Add
          </Button>
        </form>
        <Button block onClick={onClose}>
          Done
        </Button>
      </div>
    </Modal>
  );
}
